import { LoaderFunction, Outlet, redirect } from "react-router-dom";

import { isNativeMobileApp } from "@/utils/platform.helpers";
import { getPlatformCurrent, getUser } from "api/methods";
import { isAnonymousBrowsing } from "modules/anonymousBrowsing";
import { queryClient } from "pages/Root";
import { identifyFlagVisitor } from "services/Flags";

import { V3ReferralOfferForNewClientsPage_PATH } from "./signIn/V3ReferralOfferForNewClientsPage";

// Sign-in screens a signed-in patient still walks: adding another practice on the native app runs
// practice search, location and source, and the profile screens when the practice picker finds a
// field the backend's completed-profile rule does not require (gender).
const SIGNED_IN_PATIENT_PATHS = new Set([
  "/signin/platform-search",
  "/signin/location",
  "/signin/source",
  "/signin/name",
  "/signin/email",
  "/signin/dob",
  "/signin/gender",
  V3ReferralOfferForNewClientsPage_PATH,
]);

export const anonymousLoader: LoaderFunction = async ({ request }) => {
  const url = new URL(request.url);
  const pathname = url.pathname.replace(/\/$/, "");
  // Parent and child loaders run concurrently. The impersonation loader owns
  // the identity switch; reading the previous user here can redirect before it finishes.
  if (pathname === "/admin-impersonate") {
    return null;
  }
  const successfulPaymentParam = url.searchParams.get("successfulPayment");

  const platform = await queryClient.fetchQuery({
    ...getPlatformCurrent.keyed(),
    queryFn: () => getPlatformCurrent().catch(() => null),
  });

  // No token means nobody signed in; the real `/user` check only runs when a
  // token exists (anonymous browsing is off then).
  const user = isAnonymousBrowsing()
    ? null
    : await queryClient.fetchQuery({ ...getUser.keyed() }).catch(() => null);

  // Ensure we have a flag context for targeting rules; a signed-in patient keeps their own
  identifyFlagVisitor(user, platform);

  // If the platform is not active, redirect to the deactivated page
  if (platform?.active === false) {
    if ((isNativeMobileApp() && !request.url.includes("/signin")) || !isNativeMobileApp()) {
      return redirect("/deactivated");
    }

    return null;
  }

  if (
    user &&
    user.completedProfile &&
    !successfulPaymentParam &&
    !SIGNED_IN_PATIENT_PATHS.has(pathname)
  ) {
    return redirect("/home");
  }

  return null;
};

/**
 * Layout route for anonymous users (pre-login)
 */
const AnonymousLayout = () => {
  return (
    <div className="flex flex-auto">
      <div className="w-full">
        <Outlet />
      </div>
    </div>
  );
};

export default AnonymousLayout;
